import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import Hr from '../../../components/Hr';
import { distances, border, colors } from '../../../styles/constants';
import Card from '../../../components/Card';
import { H3 } from '../../../components/Typography';
import { OrderWithEvents, OrderAuthorization, OrderCapture, OrderRefund, OrderCancellation } from '../../../types/orders';
import { TFunction } from 'i18next';
import { Money } from '../../../components/Formatters';
import { Dl, Dt, Dd } from '../../../components/DescriptionList/Vertical';
import { Table, Thead, Tr, Th, Td, Tbody } from '../../../components/Table';
import { isSuperset } from '../../../helpers/sets';

import TransactionId from '../../common/TransactionId';
import ExpandCollapse from '../../common/ExpandCollapse';
import Pan from '../../../components/Formatters/Pan';
import {
    getOperationAmount,
    getItemAuthorizedAmount,
    getItemCapturedAmount,
    getItemRefundedAmount
} from '../../helpers';

export interface OrderPaymentsProps extends WithTranslation {
    order: OrderWithEvents;
}

export interface PaymentGroup {
    authorizations: OrderAuthorization[];
    captures: OrderCapture[];
    refunds: OrderRefund[];
    cancellations: OrderCancellation[];
}

type OperationCollection = OrderAuthorization[] | OrderCapture[] | OrderRefund[] | OrderCancellation[];

const getItems = (group: PaymentGroup) => {
    const values = [
        ...(group.authorizations || []),
        ...(group.captures || []),
        ...(group.refunds || []),
        ...(group.cancellations || []),
    ]
        .filter((operation) => operation)
        .map((operation) => operation.items)
        .reduce((acc, items) => [...acc, ...items], [])
        .map((item) => item.line_id);
    return new Set(values);
};

const getEarliestOperationTimestamp = (collection: OperationCollection | undefined) => {
    return (collection || []).reduce((earliest, operation) => {
        const time = operation.processed_at ? new Date(operation.processed_at).getTime() : 0;
        if (earliest === 0) {
            return time;
        }
        return earliest > time ? time : earliest;
    }, 0);
};

const getEarliestGroupTimestamp = (group: PaymentGroup) => {
    return (
        [group.authorizations, group.captures, group.refunds, group.cancellations]
            .filter((x) => x)
            .map((collection) => getEarliestOperationTimestamp(collection))
            .sort()
            .find((x) => x) || 0
    );
};

const groupByPaymentDetailsId = (order: OrderWithEvents): PaymentGroup[] => {
    const byPaymentDetailsPaymentId: { [paymentDetailsPaymentId: string]: PaymentGroup } = {};
    (order.authorizations || []).forEach((authorization) => {
        const id = authorization?.payment_details?.payment_id || '';
        byPaymentDetailsPaymentId[id] = {
            ...byPaymentDetailsPaymentId[id],
            authorizations: [
                ...((byPaymentDetailsPaymentId[id] && byPaymentDetailsPaymentId[id].authorizations) || []),
                authorization,
            ],
        };
    });
    (order.captures || []).forEach((capture) => {
        const id = capture?.payment_details?.payment_id || '';
        byPaymentDetailsPaymentId[id] = {
            ...byPaymentDetailsPaymentId[id],
            captures: [...((byPaymentDetailsPaymentId[id] && byPaymentDetailsPaymentId[id].captures) || []), capture],
        };
    });
    (order.refunds || []).forEach((refund) => {
        const id = refund?.payment_details?.payment_id || '';
        byPaymentDetailsPaymentId[id] = {
            ...byPaymentDetailsPaymentId[id],
            refunds: [...((byPaymentDetailsPaymentId[id] && byPaymentDetailsPaymentId[id].refunds) || []), refund],
        };
    });
    (order.cancellations || []).forEach((cancellation) => {
        const id = cancellation?.payment_details?.payment_id || '';
        byPaymentDetailsPaymentId[id] = {
            ...byPaymentDetailsPaymentId[id],
            cancellations: [
                ...((byPaymentDetailsPaymentId[id] && byPaymentDetailsPaymentId[id].cancellations) || []),
                cancellation,
            ],
        };
    });

    const entries = Object.entries(byPaymentDetailsPaymentId);
    if (byPaymentDetailsPaymentId[''] && entries.length > 1) {
        // If there are operations without a transaction id, but they have the
        // same items ( or a subset) as an operation group with an transaction id,
        // add the non-id operations to the group with a transaction id.
        const noTxnGroup = byPaymentDetailsPaymentId[''];
        const noTxnItemLineIds = getItems(noTxnGroup);
        const withIdEntries = entries.filter(([key, group]) => key);

        withIdEntries.forEach(([key, group]) => {
            const itemLineIds = getItems(group);
            if (isSuperset(itemLineIds, noTxnItemLineIds)) {
                if (noTxnGroup.authorizations) {
                    if (group.authorizations) {
                        group.authorizations.push(...noTxnGroup.authorizations);
                    } else {
                        group.authorizations = noTxnGroup.authorizations;
                    }
                }
                if (noTxnGroup.captures) {
                    if (group.captures) {
                        group.captures.push(...noTxnGroup.captures);
                    } else {
                        group.captures = noTxnGroup.captures;
                    }
                }
                if (noTxnGroup.refunds) {
                    if (group.refunds) {
                        group.refunds.push(...noTxnGroup.refunds);
                    } else {
                        group.refunds = noTxnGroup.refunds;
                    }
                }
                if (noTxnGroup.cancellations) {
                    if (group.cancellations) {
                        group.cancellations.push(...noTxnGroup.cancellations);
                    } else {
                        group.cancellations = noTxnGroup.cancellations;
                    }
                }
            }
            delete byPaymentDetailsPaymentId[''];
        });
    }
    return Object.values(byPaymentDetailsPaymentId).sort(
        (a, b) => getEarliestGroupTimestamp(a) - getEarliestGroupTimestamp(b)
    );
};

interface PaymentDetails {
    /**
     * example:
     * Walley
     */
    psp?: string;
    payment_id?: string;
    /**
     * example:
     * collector
     */
    payment_product?: string;
    /**
     * example:
     * collector.finance
     */
    payment_product_type?: string;
    card?: {
        [name: string]: string | undefined;
        masked_pan?: string;
        /**
         * example:
         * Credit
         */
        type?: string;
        /**
         * example:
         * Visa
         */
        brand?: string;
    };
}

const getOperationPaymentDetails = (group: PaymentGroup): PaymentDetails | undefined => {
    return ([group.authorizations, group.captures, group.refunds, group.cancellations] as OperationCollection[])
        .filter((x) => x)
        .reduce<any>((id, collection) => {
            return (
                id || collection.map((operation) => operation?.payment_details || undefined).find((x) => x) || undefined
            );
        }, undefined);
};

const getOperationTotalAmount = (collection: OperationCollection | undefined) => {
    return (collection || []).reduce((sum, operation) => sum + getOperationAmount(operation), 0);
};

const OperationSum = (
    {
        t,
        label,
        amount,
        currency,
    }: {
        t: TFunction;
        label: string;
        amount: number;
        currency: string;
    }
) => {
    return (
        <OperationSumWrapper>
            <span>{label}</span>
            <Money amount={amount} currency={currency} />
        </OperationSumWrapper>
    );
};

const PaymentType = (
    {
        t,
        details,
    }: {
        t: TFunction;
        details: PaymentDetails | undefined;
    }
) => {
    if (!details) {
        return <>N/A</>;
    }
    const parts = [];
    if (details.psp) {
        parts.push(details.psp);
    }
    if (details.payment_product) {
        const pp = t([`payments.payment_product.${details.payment_product}`, details.payment_product]);
        // Only add ppt if it is different from psp
        if (!parts.includes(pp)) {
            parts.push(pp);
        }
    }
    if (details.payment_product_type) {
        const ppt = t([`payments.product_types.${details.payment_product_type}`, details.payment_product_type]);
        // Only add ppt if it is different from psp and payment_product
        if (!parts.includes(ppt)) {
            parts.push(ppt);
        }
    }
    const card = details.card && [details.card.brand, details.card.type].filter((x) => x).join(', ');
    return (
        <>
            {parts.join(', ')}
            {card && (
                <>
                    <br /> {card}
                    {details.card?.masked_pan && (
                        <>
                            {card ? ', ' : ''}
                            <Pan value={details.card?.masked_pan} />
                        </>
                    )}
                </>
            )}
        </>
    );
};

const PaymentGroupState = (
    {
        t,
        group,
    }: {
        t: TFunction;
        group: PaymentGroup;
    }
): JSX.Element => {
    const authorized = getOperationTotalAmount(group.authorizations);
    const captured = getOperationTotalAmount(group.captures);
    const refunded = getOperationTotalAmount(group.refunds);
    const cancelled = getOperationTotalAmount(group.cancellations);

    if (authorized > 0 && captured === 0 && refunded === 0 && cancelled === 0) {
        return t('orders.order.payments.state.authorized');
    }
    if (authorized > 0 && captured > 0 && authorized === captured && refunded === 0 && cancelled === 0) {
        return t('orders.order.payments.state.captured');
    }
    if (authorized > 0 && captured > 0 && authorized !== captured && refunded === 0 && cancelled === 0) {
        return t('orders.order.payments.state.part_captured');
    }
    if (authorized > 0 && captured > 0 && authorized !== captured && refunded > 0 && cancelled === 0) {
        return t('orders.order.payments.state.part_captured_part_refunded');
    }
    if (
        authorized > 0 &&
        captured > 0 &&
        refunded > 0 &&
        authorized === captured &&
        captured === refunded &&
        cancelled === 0
    ) {
        return t('orders.order.payments.state.refunded');
    }
    if (
        authorized > 0 &&
        captured > 0 &&
        refunded > 0 &&
        authorized === captured &&
        captured !== refunded &&
        cancelled === 0
    ) {
        return t('orders.order.payments.state.part_refunded');
    }
    if (authorized > 0 && authorized > 0 && cancelled > 0 && authorized !== cancelled) {
        // Shouldn't be possible with a Dintero transaction but could perhaps occur from
        // other gateways
        return t('orders.order.payments.state.part_cancelled');
    }
    if (authorized > 0 && authorized > 0 && cancelled > 0 && authorized === cancelled) {
        return t('orders.order.payments.state.cancelled');
    }
    return t('orders.order.payments.state.unknown');
};


const StatusItems = (
    {
        t,
        order,
    }: {
        t: TFunction;
        order: OrderWithEvents;
    }
) => {
    return (
        <ItemField>
            <Table>
                <Thead>
                    <Tr>
                        <Th>{t('orders.fields.line_item_id')}</Th>
                        <Th className="align-right">{t('orders.order.payments.authorized')}</Th>
                        <Th className="align-right">{t('orders.order.payments.captured')}</Th>
                        <Th className="align-right">{t('orders.order.payments.refunded')}</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {(order.items || []).map((item, i) => (
                        <Tr key={[item.id, i].filter(x => x).join('-')}>
                            <Td>{item.line_id}</Td>
                            <Td className="align-right">
                                <Money
                                    amount={getItemAuthorizedAmount(order, item.line_id || -1)}
                                    currency={order.currency}
                                />
                            </Td>
                            <Td className="align-right">
                                <Money
                                    amount={getItemCapturedAmount(order, item.line_id || -1)}
                                    currency={order.currency}
                                />
                            </Td>
                            <Td className="align-right">
                                <Money
                                    amount={getItemRefundedAmount(order, item.line_id || -1)}
                                    currency={order.currency}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </ItemField>
    );
};

const ExpandCollapseDetails = (
    {
        t,
        group,
        order,
    }: {
        t: TFunction;
        group: PaymentGroup;
        order: OrderWithEvents;
    }
) => {
    return (
        <ExpandCollapse
            label={<strong>{t('orders.order.payments.click_to_expand_details')}</strong>}
            content={
                <>
                    <Dl>
                        <Dt>{t('orders.order.payments.amounts_per_operation_type')}</Dt>
                        <Dd>
                            <OperationSum
                                t={t}
                                label={t('orders.order.payments.authorized')}
                                amount={getOperationTotalAmount(group.authorizations)}
                                currency={order.currency}
                            />
                            <OperationSum
                                t={t}
                                label={t('orders.order.payments.captured')}
                                amount={getOperationTotalAmount(group.captures)}
                                currency={order.currency}
                            />
                            <OperationSum
                                t={t}
                                label={t('orders.order.payments.refunded')}
                                amount={getOperationTotalAmount(group.refunds)}
                                currency={order.currency}
                            />
                            <OperationSum
                                t={t}
                                label={t('orders.order.payments.cancelled')}
                                amount={getOperationTotalAmount(group.cancellations)}
                                currency={order.currency}
                            />
                        </Dd>
                    </Dl>
                    <Hr />
                    <Dl>
                        <Dt>{t('orders.order.payments.items')}</Dt>
                        <Dd>
                            <StatusItems t={t} order={order} />
                        </Dd>
                    </Dl>
                </>
            }
        />
    );
};

const PaymentGroupItem = (
    {
        t,
        group,
        order,
    }: {
        t: TFunction;
        group: PaymentGroup;
        order: OrderWithEvents;
    }
) => {
    const details = getOperationPaymentDetails(group);
    const authorized = getOperationTotalAmount(group.authorizations);
    return (
        <>
            <HalfNHalf>
                <Dl>
                    <Dt>{t('orders.order.payments.transaction_id')}</Dt>
                    <Dd>
                        <TransactionId transactionId={details?.payment_id} fallback="N/A" />
                    </Dd>
                </Dl>
                <Dl>
                    <Dt>{t('orders.order.payments.transaction_state')}</Dt>
                    <Dd>
                        <PaymentGroupState t={t} group={group} />
                    </Dd>
                </Dl>
            </HalfNHalf>
            <HalfNHalf>
                <Dl>
                    <Dt>{t('orders.order.payments.payment_type')}</Dt>
                    <Dd>
                        <PaymentType t={t} details={details} />
                    </Dd>
                </Dl>
                <Dl>
                    <Dt>{t('orders.order.payments.amount')}</Dt>
                    <Dd>
                        <Money amount={authorized} currency={order.currency} />
                    </Dd>
                </Dl>
            </HalfNHalf>
            <ExpandCollapseDetails t={t} group={group} order={order} />
        </>
    );
};

const OrderPayments = (
    {
        t,
        order,
    }: OrderPaymentsProps
) => {
    const groups = groupByPaymentDetailsId(order);
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.order.payments.title')}</H3>
            <Hr />
            {groups.map((group, i) => (
                <React.Fragment key={[JSON.stringify(group), i].filter(x => x).join('-')}>
                    <PaymentGroupItem t={t} group={group} order={order} />
                    {i < groups.length - 1 && <Hr />}
                </React.Fragment>
            ))}
            {groups.length === 0 && t('orders.order.payments.no_transaction_data')}
        </Card>
    );
};

export default withTranslation()(OrderPayments);



const OperationSumWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    word-break: normal;
    white-space: nowrap;
    border-bottom: 1px dashed transparent;

    &:hover {
        border-bottom: 1px dashed #cecece;
    }
`;

const ItemField = styled.div`
    display: flex;
    justify-content: space-between;
    word-break: normal;
    white-space: nowrap;
    border-bottom: 1px dashed transparent;

    &:hover {
        border-bottom: 1px dashed #cecece;
    }
`;

const HalfNHalf = styled.div`
    display: flex;
    justify-content: space-between;
    word-break: normal;
    white-space: nowrap;
    > * {
        width: 100%;
        overflow: hidden;
        &:nth-child(odd) {
            margin-right: ${distances.tiny};
        }
        &:nth-child(even) {
            margin-left: ${distances.tiny};
        }
    }
`;
